import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getEmployeeGrowers,
  getEmployeeGrower,
  createGrower,
  updateGrower,
  patchGrower,
  deleteGrower as deleteGrowerService,
  getGrowerChoices,
} from '../services/growers';
import { logError } from '../utils/errorHandler';

/**
 * Custom hook for managing grower CRUD state
 *
 * @param {Object} options
 * @param {boolean} options.autoFetch - Fetch growers + choices on mount (default: true)
 * @returns {Object} State and action functions
 */
const useGrowers = ({ autoFetch = true } = {}) => {
  const [growers, setGrowers] = useState([]);
  const [loading, setLoading] = useState(autoFetch);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ count: 0, page: 1 });
  const [choices, setChoices] = useState(null);
  const hasLoadedRef = useRef(false);

  /**
   * Fetch paginated growers list
   */
  const fetchGrowers = useCallback(async (params = {}) => {
    try {
      setError(null);
      if (hasLoadedRef.current) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const data = await getEmployeeGrowers(params);
      setGrowers(data.results || []);
      setPagination({
        count: data.count || 0,
        page: params.page || 1,
      });
    } catch (err) {
      logError('Fetch growers', err);
      setError(err);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  /**
   * Fetch a single grower by ID
   */
  const fetchGrower = useCallback(async (id) => {
    try {
      setError(null);
      setLoading(true);
      const data = await getEmployeeGrower(id);
      return data;
    } catch (err) {
      logError('Fetch grower', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create or update a grower
   */
  const saveGrower = useCallback(async (data, id = null) => {
    try {
      setError(null);
      setIsSaving(true);

      const result = id
        ? await updateGrower(id, data)
        : await createGrower(data);

      return result;
    } catch (err) {
      logError('Save grower', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Soft-delete a grower
   */
  const removeGrower = useCallback(async (id) => {
    try {
      setError(null);
      setIsDeleting(true);
      await deleteGrowerService(id);
    } catch (err) {
      logError('Delete grower', err);
      setError(err);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  /**
   * Reactivate a grower (PATCH is_active=true)
   */
  const reactivateGrower = useCallback(async (id) => {
    try {
      setError(null);
      setIsSaving(true);
      await patchGrower(id, { is_active: true });
    } catch (err) {
      logError('Reactivate grower', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Fetch choices for form dropdowns
   */
  const fetchChoices = useCallback(async () => {
    try {
      const data = await getGrowerChoices();
      setChoices(data);
    } catch (err) {
      logError('Fetch grower choices', err);
    }
  }, []);

  /**
   * Refresh = re-fetch current page
   */
  const refresh = useCallback((params = {}) => {
    return fetchGrowers(params);
  }, [fetchGrowers]);

  const clearError = useCallback(() => setError(null), []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchGrowers();
      fetchChoices();
    }
  }, [autoFetch, fetchGrowers, fetchChoices]);

  return {
    growers,
    loading,
    isRefreshing,
    isSaving,
    isDeleting,
    error,
    pagination,
    choices,
    fetchGrowers,
    fetchGrower,
    saveGrower,
    removeGrower,
    reactivateGrower,
    fetchChoices,
    refresh,
    clearError,
  };
};

export default useGrowers;
